import type { Locale } from "./constants";
import type { Translator } from "./i18n";
import type { LengthClass } from "./schemas/settings";

/** How a registration is written: capitals, no hyphens, no spaces ("st-12 34" → "ST1234"). */
export function normalizeRegistration(s: string): string {
  return s.toUpperCase().replace(/[\s\-\u2010-\u2015]/g, "");
}

/** 3.5 → "3,5" (hr) / "3.5" (en); trims trailing zeros, max 2 decimals. */
export function formatNumber(n: number | null | undefined, locale: Locale | string = "hr", maxDecimals = 2): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "";
  return new Intl.NumberFormat(locale === "en" ? "en-GB" : "hr-HR", { maximumFractionDigits: maxDecimals, useGrouping: false }).format(n);
}

export function formatMetres(n: number | null | undefined, locale: Locale | string = "hr", maxDecimals = 1): string {
  const s = formatNumber(n, locale, maxDecimals);
  return s ? `${s} m` : "";
}

/** Label of a length class: "do 8 m", "8–12 m", "20+ m". */
export function lengthClassLabel(classes: LengthClass[], index: number, t: Translator): string {
  const c = classes[index]!;
  const min = index === 0 ? 0 : (classes[index - 1]!.maxM ?? 0);
  const fmt = (n: number) => formatNumber(n, t.locale);
  if (index === 0 && c.maxM !== null) return t.t("lengthClass.upTo", { max: fmt(c.maxM) });
  if (c.maxM === null) return t.t("lengthClass.over", { min: fmt(min) });
  return t.t("lengthClass.range", { min: fmt(min), max: fmt(c.maxM) });
}

/** Collapse a connection list to the short form: "16 A · voda". */
export function formatConnections(conns: readonly string[], t: Translator): string {
  return conns.map((c) => t.tx(`conn.short.${c}`)).join(" · ");
}
